import type { CollectionReference, DocumentReference } from 'firebase/firestore'
import { onSnapshot } from 'firebase/firestore'
import * as logger from '../../../utils/logger'
import type {
  CollectionListenerCallback,
  DocumentListenerCallback,
} from './model/types'

type ListenerType = 'document' | 'collection'
type ListenerCallback = DocumentListenerCallback | CollectionListenerCallback

interface ListenerEntry {
  unsubscribe: () => void
  type: ListenerType
  callbackCount: number
}

class FirestoreListenersManager {
  private readonly listeners = new Map<string, ListenerEntry>()
  private readonly documentCallbacks = new Map<
    string,
    Set<DocumentListenerCallback>
  >()
  private readonly collectionCallbacks = new Map<
    string,
    Set<CollectionListenerCallback>
  >()

  /**
   * Subscribe to real-time updates for a Firestore document
   * @param docRef - Firestore document reference
   * @param callback - Function to call when document changes
   * @returns Unsubscribe function to stop listening
   */
  subscribeToDocument(
    docRef: DocumentReference,
    callback: DocumentListenerCallback,
  ): () => void {
    const path = docRef.path

    this.addDocumentCallback(path, callback)
    this.ensureDocumentListener(docRef)

    return this.createUnsubscribeFunction(path, callback, 'document')
  }

  /**
   * Subscribe to real-time updates for a Firestore collection
   * @param collectionRef - Firestore collection reference
   * @param callback - Function to call when collection changes
   * @returns Unsubscribe function to stop listening
   */
  subscribeToCollection(
    collectionRef: CollectionReference,
    callback: CollectionListenerCallback,
  ): () => void {
    const path = collectionRef.path

    this.addCollectionCallback(path, callback)
    this.ensureCollectionListener(collectionRef)

    return this.createUnsubscribeFunction(path, callback, 'collection')
  }

  /**
   * Clean up all active listeners and callbacks
   */
  cleanup(): void {
    const listenerCount = this.listeners.size

    for (const [path, listener] of this.listeners) {
      listener.unsubscribe()
      logger.info(
        `[FirestoreListenersManager] Cleaned up ${listener.type} listener: ${path}`,
      )
    }

    this.listeners.clear()
    this.documentCallbacks.clear()
    this.collectionCallbacks.clear()

    logger.info(
      `[FirestoreListenersManager] All ${listenerCount} listeners cleaned up`,
    )
  }

  // Private helper methods

  private addDocumentCallback(
    path: string,
    callback: DocumentListenerCallback,
  ): void {
    if (!this.documentCallbacks.has(path)) {
      this.documentCallbacks.set(path, new Set())
    }
    this.documentCallbacks.get(path)!.add(callback)
  }

  private addCollectionCallback(
    path: string,
    callback: CollectionListenerCallback,
  ): void {
    if (!this.collectionCallbacks.has(path)) {
      this.collectionCallbacks.set(path, new Set())
    }

    this.collectionCallbacks.get(path)!.add(callback)
  }

  private ensureDocumentListener(docRef: DocumentReference): void {
    const path = docRef.path

    if (!this.listeners.has(path)) {
      const unsubscribe = this.createDocumentListener(docRef)
      const callbackCount = this.documentCallbacks.get(path)?.size ?? 0

      this.listeners.set(path, {
        unsubscribe,
        type: 'document',
        callbackCount,
      })

      logger.info(
        `[FirestoreListenersManager] Created document listener: ${path}`,
      )
    }
  }

  private ensureCollectionListener(collectionRef: CollectionReference): void {
    const path = collectionRef.path

    if (!this.listeners.has(path)) {
      const unsubscribe = this.createCollectionListener(collectionRef)
      const callbackCount = this.collectionCallbacks.get(path)?.size ?? 0

      this.listeners.set(path, {
        unsubscribe,
        type: 'collection',
        callbackCount,
      })

      logger.info(
        `[FirestoreListenersManager] Created collection listener: ${path}`,
      )
    }
  }

  private createDocumentListener(docRef: DocumentReference): () => void {
    const path = docRef.path

    return onSnapshot(
      docRef,
      (snapshot) => {
        const callbacks = this.documentCallbacks.get(path)
        if (!callbacks) return

        this.executeCallbacks(callbacks, snapshot, `document ${path}`)
      },
      (error) => {
        logger.error(
          `[FirestoreListenersManager] Document listener error for ${path}:`,
          error,
        )
      },
    )
  }

  private createCollectionListener(
    collectionRef: CollectionReference,
  ): () => void {
    const path = collectionRef.path

    return onSnapshot(
      collectionRef,
      (snapshot) => {
        const callbacks = this.collectionCallbacks.get(path)
        if (!callbacks) return

        this.executeCallbacks(callbacks, snapshot, `collection ${path}`)
      },
      (error) => {
        logger.error(
          `[FirestoreListenersManager] Collection listener error for ${path}:`,
          error,
        )
      },
    )
  }

  private executeCallbacks<T>(
    callbacks: Set<(snapshot: T) => void>,
    snapshot: T,
    context: string,
  ): void {
    for (const callback of callbacks) {
      try {
        callback(snapshot)
      } catch (error) {
        logger.error(
          `[FirestoreListenersManager] Callback error for ${context}:`,
          error,
        )
      }
    }
  }

  private createUnsubscribeFunction(
    path: string,
    callback: ListenerCallback,
    type: ListenerType,
  ): () => void {
    return () => {
      const removed = this.removeCallback(path, callback, type)

      if (removed && this.shouldCleanupListener(path, type)) {
        this.cleanupListener(path)
      }
    }
  }

  private removeCallback(
    path: string,
    callback: ListenerCallback,
    type: ListenerType,
  ): boolean {
    if (type === 'document') {
      const callbacks = this.documentCallbacks.get(path)
      if (callbacks?.has(callback as DocumentListenerCallback)) {
        callbacks.delete(callback as DocumentListenerCallback)
        return true
      }
    } else {
      const callbacks = this.collectionCallbacks.get(path)
      if (callbacks?.has(callback as CollectionListenerCallback)) {
        callbacks.delete(callback as CollectionListenerCallback)
        return true
      }
    }
    return false
  }

  private shouldCleanupListener(path: string, type: ListenerType): boolean {
    const callbacks =
      type === 'document'
        ? this.documentCallbacks.get(path)
        : this.collectionCallbacks.get(path)

    return callbacks?.size === 0
  }

  private cleanupListener(path: string): void {
    const listener = this.listeners.get(path)
    if (!listener) return

    listener.unsubscribe()
    this.listeners.delete(path)

    // Clean up empty callback sets
    this.documentCallbacks.delete(path)
    this.collectionCallbacks.delete(path)

    logger.info(
      `[FirestoreListenersManager] Cleaned up ${listener.type} listener: ${path}`,
    )
  }
}

export const firestoreListenersManager = new FirestoreListenersManager()
